import type { LoginGrantState } from "helpers/login-grant";

import { resumeDestination, type ResumeNext } from "pages/StartResume/resume";

import { verifyPathFor } from "./destinations";
import type { Handoff } from "./handoff";
import type { LeadKind } from "./flow";
import { takeLeadReturn } from "./leadReturn";
import type { ConfirmedVia, SubmissionStatusClient } from "./submissionStatus";

const SUBMISSION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The refusal code a quote session carries while the lead behind it still
 * has to prove who they are. */
export const AWAITING_CONFIRMATION_REASON = "quote_awaiting_confirmation";

/** The submission named by a body that says a lead is awaiting confirmation,
 * or null when the id is absent or not a UUID. */
export function submissionIdOf(raw: unknown): string | null {
  if (typeof raw !== "object" || raw === null) return null;
  const id = (raw as Record<string, unknown>).submission_id;
  return typeof id === "string" && SUBMISSION_ID.test(id) ? id : null;
}

export type ConfirmationScreen =
  | {
      kind: "login";
      path: string;
      state: Pick<LoginGrantState, "loginGrant" | "leadKind">;
    }
  | {
      kind: "confirmed";
      next: ResumeNext;
      submissionId: string;
      via: ConfirmedVia;
    }
  | { kind: "interstitial" };

/** Where a visitor whose sign-in or remembered browser confirmed the submission lands:
 * as a confirmed sign-in does, on the quote or case it made, or back on the
 * page the lead left when one was stored for this submission. Reading the
 * stored return clears it. */
export function confirmedDestination(
  next: ResumeNext,
  submissionId: string,
  takeReturn: (submissionId: string) => string | null = takeLeadReturn,
): string {
  return resumeDestination(next, takeReturn(submissionId));
}

/** Where a visitor whose lead is awaiting confirmation goes: the sign-in
 * screen with a fresh grant, straight on when their remembered browser
 * already confirmed it, or, when no grant can be minted, the call-us
 * interstitial. A caller that knows what the lead was for passes `leadKind`,
 * which the sign-in screen picks its phone line from. */
export async function confirmationScreenFor(
  submissionId: string | null,
  client: Pick<SubmissionStatusClient, "mint">,
  leadKind?: LeadKind,
  handoff: Handoff | null = null,
): Promise<ConfirmationScreen> {
  if (submissionId === null) return { kind: "interstitial" };
  const answer = await client.mint(submissionId);
  if (answer.kind === "confirmed") {
    return {
      kind: "confirmed",
      next: answer.next,
      submissionId,
      via: answer.via,
    };
  }
  if (answer.kind !== "minted") return { kind: "interstitial" };
  return {
    kind: "login",
    path: verifyPathFor(submissionId, handoff),
    state: {
      loginGrant: answer.grant.grant,
      ...(leadKind ? { leadKind } : {}),
    },
  };
}
